import React, { forwardRef } from 'react';
import {
  arrayOf,
  shape,
  string,
  bool,
} from 'prop-types';
import { extend } from '@thd-nucleus/data-sources';
import classnames from 'classnames';
import {
  AssistantRecommendationsImporter,
  ATCConfirmationImporter,
  ExceptionMessageImporter,
  RecommendedSearchesImporter
} from './dynamic-imports';
import { MessageBubble } from './messageTypes/MessageBubble';
import { MessageFeedback } from './MessageFeedback';

export const ChatMessage = forwardRef(({ message, showFeedback }, ref) => {
  const { type, content, error } = message;

  const renderMessageContent = () => {
    if (type === 'user') {
      return [<MessageBubble key="user-message" type={type}>{message.content}</MessageBubble>];
    }

    if (type === 'add-to-cart') {
      return [(
        <ATCConfirmationImporter itemId={message.content.itemId} key="atc-confirmation-message" />
      )];
    }

    if (error || !content || !content?.responseSummary?.summaryAnswer) {
      // If there is an error or no content, show the error message
      return [<ExceptionMessageImporter data={{}} key="error-message" />];
    }

    const { responseSummary, relatedContent, additionalDetails } = content;

    const messages = [];

    if (responseSummary?.summaryAnswer) {
      messages.push(<MessageBubble key="summary-message" type={type}>{responseSummary?.summaryAnswer}</MessageBubble>);
    }
    if (relatedContent?.recommendations?.bestMatch?.length > 0) {
      messages.push(
        <AssistantRecommendationsImporter
          products={relatedContent.recommendations?.bestMatch}
          key="assistant-recommendations"
        />
      );
    }
    if (relatedContent?.relatedSearchResults?.length > 0) {
      messages.push(
        <RecommendedSearchesImporter key="relatedSearch-message" categories={relatedContent.relatedSearchResults} />
      );
    }
    if (additionalDetails?.inquiryDetails) {
      messages.push(<ExceptionMessageImporter key="inquiryDetails-message" data={additionalDetails.inquiryDetails} />);
    }

    return messages;
  };

  const chatMessageClasses = classnames('sui-flex sui-flex-col sui-mb-3', {
    'sui-align-end': type === 'user',
    'sui-align-start': type === 'bot'
  });

  return (
    <div data-testid="chat-message" className={chatMessageClasses} ref={ref}>
      {renderMessageContent()}
      {showFeedback && (<MessageFeedback message={message} />)}
    </div>
  );
});

ChatMessage.propTypes = {
  showFeedback: bool,
  message: shape({
    relatedContent: shape({
      relatedSearchResults: arrayOf(shape({
        itemId: string,
        price: string,
        imageUrl: string,
        rating: string,
        description: string,
        href: string,
        category: arrayOf(string),
        searchTerm: string
      })),
      recommendations: shape({
        bestMatch: arrayOf(shape({
          itemId: string,
          price: string,
          averageRating: string,
          imageUrl: string,
          href: string,
          productName: string
        }))
      })
    }),
    suggestedQuestions: arrayOf(string),
    followUpQuestions: arrayOf(string),
    additionalDetails: shape({
      inquiryDetails: shape({
        telephoneNumber: string,
        description: string,
        referenceUrl: string
      })
    })
  }).isRequired,
};

ChatMessage.defaultProps = {
  showFeedback: false,
};

ChatMessage.dataModel = extend(
  MessageFeedback
);
